import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Language } from '../config/translations';

interface LanguageSwitcherProps {
  language: Language;
  onLanguageChange: (lang: Language) => void;
}

const languageOptions = [
  { code: 'en' as Language, label: 'EN', fullName: 'English' },
  { code: 'de' as Language, label: 'DE', fullName: 'Deutsch' },
  { code: 'es' as Language, label: 'ES', fullName: 'Español' },
  { code: 'sv' as Language, label: 'SV', fullName: 'Svenska' }
];

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ language, onLanguageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languageOptions.find(lang => lang.code === language);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX
      });
    }
    setIsOpen(!isOpen);
  };

  const dropdownContent = isOpen ? (
    <div 
      ref={dropdownRef}
      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 min-w-[120px] overflow-hidden"
      style={{ 
        zIndex: 999999,
        top: dropdownPosition.top,
        left: dropdownPosition.left
      }}
    >
      {languageOptions.map((option) => (
        <button
          key={option.code}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Language option clicked:', option.code);
            onLanguageChange(option.code);
            setIsOpen(false);
          }}
          className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-150 flex items-center justify-between cursor-pointer ${
            language === option.code 
              ? 'bg-blue-100 text-blue-700 font-medium' 
              : 'text-gray-700 hover:text-gray-900'
          }`}
          type="button"
        >
          <span className="text-sm">{option.fullName}</span>
          <span className="text-xs font-semibold text-gray-500">{option.label}</span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white font-medium hover:bg-white/20 transition-all duration-200 flex items-center gap-2 min-w-[70px]"
        title={currentLanguage?.fullName}
      >
        <span className="text-sm">{currentLanguage?.label}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {typeof document !== 'undefined' && dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
};

export default LanguageSwitcher;

